import { Suspense, createElement, lazy } from 'react'

import type { DomainManifest } from 'modules/core/lib/manifests/types'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import { FilterEmployeeListActivity } from 'modules/shift_management/activities/FilterEmployeeList'

// Lazy-loaded to avoid a circular dependency.
// Type-only import so the orphan checker sees the file as used.
// The actual runtime import uses a variable to avoid the circular dependency:
// core/manifests → shift_management/manifest → OpenOneShiftReplacement → ai/useOne → core
import type {} from 'modules/shift_management/activities/OpenOneShiftReplacement'

const openOneShiftReplacementPath = 'modules/shift_management/activities/OpenOneShiftReplacement'
const LazyOpenOneShiftReplacement = lazy(() => import(openOneShiftReplacementPath))

const OpenOneShiftReplacementActivity = (props: { disruptedShiftId: string }) =>
  createElement(Suspense, { fallback: null }, createElement(LazyOpenOneShiftReplacement, props))

export const manifest = {
  sidebar: (_navigation: Navigation<NavigationPaths>) => ({
    items: [],
  }),
  activities: {
    filterEmployeeList: {
      component: FilterEmployeeListActivity,
      availableForOne: true,
      guidelines:
        'Applies filters on the shift management page: navigates to the specified date and optionally filters by location, team, and/or specific employees. Use when the user asks to show shifts for a specific date, team, location, or set of employees. This activity is headless — it applies the filters silently without showing a dialog.',
      params: {
        date: {
          guidelines:
            'The date to navigate to in YYYY-MM-DD format. Extract from the user request. If the user says "this week" or "next week", resolve to the Monday of that week. If no date is mentioned, use today.',
        },
        locationId: {
          guidelines:
            'Optional. The ID of the location to filter by. Use fetchLocations or available shift tools to resolve location names to IDs. Omit if no location filter is requested.',
        },
        teamId: {
          guidelines:
            'Optional. The ID of the team to filter by. Use available tools to resolve team names to IDs. Omit if no team filter is requested.',
        },
        employeeIds: {
          guidelines:
            'Optional. Comma-separated employee IDs to filter the shift view. Use fetchEmployees to resolve employee names to IDs. Omit if no specific employees are mentioned.',
        },
      },
    },
    openOneShiftReplacement: {
      component: OpenOneShiftReplacementActivity,
      guidelines:
        'Opens One for a disrupted shift replacement flow. Use when the user must review or start replacement work for a disrupted shift from an in-app notification.',
      params: {
        disruptedShiftId: {
          guidelines: 'The disrupted shift ID to open One for.',
        },
      },
    },
  },
} as const satisfies DomainManifest
